import { useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatMoney } from '@/lib/money';
import { qk } from '@/lib/queryKeys';
import { DataState } from '@/components/common/DataState';

interface Invoice {
  id: string;
  createdAt: string;
  amount: number;
  currency: string;
  status: 'paid' | 'pending' | 'failed';
  gateway: string;
  invoiceUrl?: string;
  description?: string;
}

interface InvoicesPage {
  items: Invoice[];
  totalPages: number;
}

export default function BillingInvoicesPage() {
  const [page, setPage] = useState(1);

  const invoicesQuery = useQuery({
    queryKey: qk.invoices(page),
    queryFn: async (): Promise<InvoicesPage> => {
      const res = await api.get('/billing/invoices', { params: { page, limit: 20 } });
      return {
        items: res.data.data ?? [],
        totalPages: res.data.meta?.totalPages ?? 1,
      };
    },
    // Keep the current page visible while the next page loads (no blank flash).
    placeholderData: keepPreviousData,
  });

  const statusColors: Record<string, string> = {
    paid: 'bg-green-100 text-green-800',
    pending: 'bg-yellow-100 text-yellow-800',
    failed: 'bg-red-100 text-red-800',
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold">Invoices</h1>
        <p className="text-gray-500">View and download your billing invoices.</p>
      </div>

      <DataState query={invoicesQuery}>
        {({ items: invoices, totalPages }) => (
          <>
            <div className="rounded-lg border">
              {/* Table Header */}
              <div className="grid grid-cols-5 gap-4 border-b bg-gray-50 px-6 py-3 text-sm font-medium text-gray-500">
                <span>Date</span>
                <span>Amount</span>
                <span>Status</span>
                <span>Gateway</span>
                <span>Actions</span>
              </div>

              {/* Table Body */}
              {invoices.length === 0 ? (
                <div className="px-6 py-12 text-center text-gray-400">No invoices found.</div>
              ) : (
                <div className="divide-y">
                  {invoices.map((invoice) => {
                    return (
                      <div key={invoice.id} className="grid grid-cols-5 gap-4 px-6 py-4 text-sm">
                        <span>{new Date(invoice.createdAt).toLocaleDateString()}</span>
                        <span className="font-medium">
                          {formatMoney(invoice.amount, invoice.currency, { alwaysDecimals: true })}
                        </span>
                        <span>
                          <span
                            className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium capitalize ${statusColors[invoice.status] ?? 'bg-gray-100 text-gray-800'}`}
                          >
                            {invoice.status}
                          </span>
                        </span>
                        <span className="capitalize text-gray-500">{invoice.gateway}</span>
                        <span>
                          {invoice.invoiceUrl ? (
                            <a
                              href={invoice.invoiceUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-700 hover:underline"
                            >
                              Download
                            </a>
                          ) : (
                            <span className="text-gray-300">&mdash;</span>
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="rounded-md border px-3 py-1 text-sm hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-500">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="rounded-md border px-3 py-1 text-sm hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </DataState>
    </div>
  );
}
